import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight, Apple } from 'lucide-react'

interface Etapa8Props {
  formData: any
  onChange: (field: string, value: any) => void
  onNext: () => void
  onPrev: () => void
  onSubmit: () => void
  saving: boolean
  submitting: boolean
}

export function Etapa8Nutricao({ formData, onChange, onNext, onPrev, onSubmit, saving, submitting }: Etapa8Props) {
  const nutricao = formData.nutricao || {}

  // Função de validação com feedback
  const validateAndProceed = () => {
    const errors: string[] = []

    // Validar declaração de veracidade
    if (!formData.veracidade_etapa8) {
      errors.push('Declaração de veracidade')
    }

    // Validar campos obrigatórios
    if (!nutricao.peso) errors.push('Peso')
    if (!nutricao.altura) errors.push('Altura')
    if (!nutricao.dieta) errors.push('Tipo de dieta')
    if (nutricao.engasgos === undefined) errors.push('Informações sobre engasgos')
    if (nutricao.sonda === undefined) errors.push('Informações sobre sonda')
    if (nutricao.perda_peso === undefined) errors.push('Informações sobre perda de peso')

    if (errors.length > 0) {
      alert(`Por favor, preencha os campos obrigatórios:\n\n${errors.join('\n')}`)
      return
    }

    onNext()
  }

  const handleNutricaoChange = (campo: string, valor: any) => {
    onChange('nutricao', {
      ...nutricao,
      [campo]: valor
    })
  }

  // Calcular IMC
  const calcularIMC = () => {
    const peso = parseFloat(nutricao.peso || '0')
    const altura = parseFloat(nutricao.altura || '0')
    
    if (peso > 0 && altura > 0) {
      const alturaEmMetros = altura / 100
      return (peso / (alturaEmMetros * alturaEmMetros)).toFixed(1)
    }
    return null
  }

  const imc = calcularIMC()

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Apple className="h-5 w-5" />
            Nutrição
          </CardTitle>
          <p className="text-sm text-gray-600">
            Avalie os dados nutricionais do idoso(a).
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="peso">Peso (kg)</Label>
              <Input
                id="peso"
                type="number"
                step="0.1"
                value={nutricao.peso || ''}
                onChange={(e) => handleNutricaoChange('peso', e.target.value)}
                placeholder="Ex: 65.5"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="altura">Altura (cm)</Label>
              <Input
                id="altura"
                type="number"
                step="0.1"
                value={nutricao.altura || ''}
                onChange={(e) => handleNutricaoChange('altura', e.target.value)}
                placeholder="Ex: 165"
                className="mt-1"
              />
            </div>
          </div>

          {imc && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>IMC: {imc}</strong>
                {parseFloat(imc) < 18.5 && ' - Abaixo do peso'}
                {parseFloat(imc) >= 18.5 && parseFloat(imc) < 25 && ' - Peso normal'}
                {parseFloat(imc) >= 25 && parseFloat(imc) < 30 && ' - Sobrepeso'}
                {parseFloat(imc) >= 30 && ' - Obesidade'}
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="dieta">Tipo de Dieta</Label>
            <Select
              value={nutricao.dieta || ''}
              onValueChange={(value) => handleNutricaoChange('dieta', value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="hipossodica">Hipossódica (pouco sal)</SelectItem>
                <SelectItem value="hipoglicemica">Hipoglicêmica (pouco açúcar)</SelectItem>
                <SelectItem value="hipolipidica">Hipolipídica (pouca gordura)</SelectItem>
                <SelectItem value="diabetica">Diabética</SelectItem>
                <SelectItem value="liquida">Líquida</SelectItem>
                <SelectItem value="pastosa">Pastosa</SelectItem>
                <SelectItem value="branda">Branda</SelectItem>
                <SelectItem value="vegetariana">Vegetariana</SelectItem>
                <SelectItem value="vegana">Vegana</SelectItem>
                <SelectItem value="outra">Outra</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="engasgos">Apresenta engasgos</Label>
              <Select
                value={nutricao.engasgos ? 'sim' : 'nao'}
                onValueChange={(value) => handleNutricaoChange('engasgos', value === 'sim')}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sim">Sim</SelectItem>
                  <SelectItem value="nao">Não</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="sonda">Usa sonda alimentação</Label>
              <Select
                value={nutricao.sonda ? 'sim' : 'nao'}
                onValueChange={(value) => handleNutricaoChange('sonda', value === 'sim')}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sim">Sim</SelectItem>
                  <SelectItem value="nao">Não</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="perda_peso">Apresentou perda de peso</Label>
              <Select
                value={nutricao.perda_peso ? 'sim' : 'nao'}
                onValueChange={(value) => handleNutricaoChange('perda_peso', value === 'sim')}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sim">Sim</SelectItem>
                  <SelectItem value="nao">Não</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="observacoes_nutricao">Observações nutricionais</Label>
            <Textarea
              id="observacoes_nutricao"
              value={nutricao.observacoes || ''}
              onChange={(e) => handleNutricaoChange('observacoes', e.target.value)}
              placeholder="Descreva aspectos importantes sobre alimentação, preferências, restrições, etc."
              className="mt-1"
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Termo de Veracidade</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Declaro que as informações prestadas nesta avaliação funcional são verdadeiras e 
              foram fornecidas com base no meu conhecimento sobre a capacidade do idoso(a).
            </p>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="veracidade_etapa8"
                checked={formData.veracidade_etapa8 || false}
                onCheckedChange={(checked) => onChange('veracidade_etapa8', checked as boolean)}
                className="!rounded-full data-[state=checked]:!bg-green-600 data-[state=checked]:!border-green-600"
              />
              <Label htmlFor="veracidade_etapa8" className="text-sm font-medium">
                Declaro que as informações são verdadeiras *
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrev}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Anterior
        </Button>
        <Button onClick={validateAndProceed}>
          Próxima Etapa
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  )
}
